import React, { useCallback, useRef, useState } from 'react';
import FileDropZone from '../shared/FileDropZone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCloudArrowUp, faDownload, faExclamationTriangle, faFileLines,
    faInfoCircle, faSpinner, faTimes, faCheckCircle, faClock,
    faQuestionCircle, faArrowRight, faUpload,
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../utils/axiosConfig';
import '../Invoicing/CSVUploadModal.css';
import './CRMCreditBulkUploadModal.css';

/**
 * Bulk credit-terms upload — preview, then confirm.
 *
 * Deliberately NOT the shared CSVUploadModal: that one posts and is done. This
 * file moves dozens of trading limits at once, so the user sees the exact plan
 * (old value → new value, per partner) and presses Apply on the thing they just
 * read. The preview call writes nothing server-side.
 */

const ENDPOINT = '/api/crm/profiles/credit-bulk-upload/';

const TEMPLATE_COLUMNS = [
    'Partner Number', 'Legal Entity Name', 'Partner Credit Days', 'Credit Limit', 'Risk Rating',
];
const TEMPLATE_EXAMPLE = ['4200', 'ACME Bunkering Pte Ltd', '30', '250000', 'Low'];

const OUTCOME_META = {
    update: { label: 'Applies', className: 'ccb-badge-apply', icon: faCheckCircle },
    approval: { label: 'Needs credit approval', className: 'ccb-badge-approval', icon: faClock },
    pending_request: { label: 'Already awaiting approval', className: 'ccb-badge-blocked', icon: faExclamationTriangle },
    unmatched: { label: 'Not found', className: 'ccb-badge-unmatched', icon: faQuestionCircle },
    archived: { label: 'Archived — skipped', className: 'ccb-badge-unmatched', icon: faQuestionCircle },
    no_change: { label: 'Already matches', className: 'ccb-badge-neutral', icon: faCheckCircle },
    no_values: { label: 'No values', className: 'ccb-badge-neutral', icon: faQuestionCircle },
};

const CRMCreditBulkUploadModal = ({ isOpen, onClose, onApplied }) => {
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [rowErrors, setRowErrors] = useState([]);
    const fileInputRef = useRef(null);

    const reset = useCallback(() => {
        setFile(null);
        setPreview(null);
        setResult(null);
        setError(null);
        setRowErrors([]);
        setBusy(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, []);

    const handleClose = useCallback(() => {
        reset();
        onClose();
    }, [reset, onClose]);

    const selectFile = (chosen) => {
        if (!chosen) return;
        if (!chosen.name.toLowerCase().endsWith('.csv')) {
            setError('Please select a CSV file.');
            return;
        }
        setFile(chosen);
        setPreview(null);
        setResult(null);
        setError(null);
        setRowErrors([]);
    };

    // The preview and the apply share one failure path so a refusal reads the
    // same either way: the row errors are the message, never a bare "failed".
    const post = async (isPreview) => {
        const payload = new FormData();
        payload.append('file', file);
        if (isPreview) payload.append('preview', 'true');
        setBusy(true);
        setError(null);
        setRowErrors([]);
        try {
            const response = await axiosInstance.post(ENDPOINT, payload, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return response.data;
        } catch (err) {
            const data = err.response?.data;
            if (data?.errors?.length) {
                setRowErrors(data.errors);
                setError(data.message || 'Some rows could not be read. Nothing was changed.');
            } else {
                setError(data?.error || 'Upload failed. Please check the file and try again.');
            }
            return null;
        } finally {
            setBusy(false);
        }
    };

    const handlePreview = async () => {
        const data = await post(true);
        if (data) setPreview(data);
    };

    const handleApply = async () => {
        const data = await post(false);
        if (!data) return;
        setResult(data);
        setPreview(null);
        if (onApplied) onApplied(data);
    };

    const handleDownloadTemplate = () => {
        const lines = [TEMPLATE_COLUMNS.join(';'), TEMPLATE_EXAMPLE.join(';')];
        const blob = new Blob(['﻿' + lines.join('\n') + '\n'], {
            type: 'text/csv;charset=utf-8;',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'HMS_Credit_Terms_Template.csv';
        link.click();
        URL.revokeObjectURL(url);
    };

    if (!isOpen) return null;

    const summary = preview?.summary || result?.summary;
    const plan = preview?.plan || result?.plan || [];

    return (
        <div className="csv-modal-overlay" onClick={handleClose}>
            <div className="csv-modal ccb-modal" onClick={(e) => e.stopPropagation()}>

                <div className="csv-modal-header">
                    <div className="csv-modal-header-text">
                        <FontAwesomeIcon icon={faCloudArrowUp} className="csv-modal-header-icon" />
                        <h2>Update Credit Terms</h2>
                    </div>
                    <button className="csv-modal-close" onClick={handleClose} aria-label="Close">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="csv-modal-body">

                    {!preview && !result && (
                        <>
                            <div className="csv-template-section">
                                <div className="csv-template-header">
                                    <FontAwesomeIcon icon={faInfoCircle} />
                                    <span>What this does</span>
                                </div>
                                <p className="csv-template-desc">
                                    Updates credit on partners you already hold, found by Partner Number
                                    (when the name agrees) or by name. Extra columns in your file are ignored.
                                </p>
                                <div className="csv-template-columns">
                                    {TEMPLATE_COLUMNS.map(col => (
                                        <span key={col} className="csv-column-tag">{col}</span>
                                    ))}
                                </div>
                                <ul className="csv-template-notes">
                                    <li>A row that matches no partner is listed for you — nothing is created.</li>
                                    <li>A blank cell leaves the stored value alone.</li>
                                    <li>Credit days, risk rating and limit reductions apply straight away.</li>
                                    <li>A limit increase is sent to a Credit Approver first.</li>
                                    <li>Credit Limit may be written 250000, 250,000 or $250.000. Risk Rating accepts Med.</li>
                                    <li>Partners stay Approved — this never sends a partner back to Pending.</li>
                                </ul>
                                <button type="button" className="csv-download-template-btn" onClick={handleDownloadTemplate}>
                                    <FontAwesomeIcon icon={faDownload} /> Download Template CSV
                                </button>
                            </div>

                            <div className="csv-form-group">
                                <label className="csv-form-label">Upload File</label>
                                <FileDropZone
                                    className={`csv-dropzone ${file ? 'has-file' : ''}`}
                                    activeClassName="drag-over"
                                    onFiles={(files) => selectFile(files[0])}
                                    onError={setError}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".csv"
                                        onChange={(e) => selectFile(e.target.files[0])}
                                        hidden
                                    />
                                    {file ? (
                                        <div className="csv-file-info">
                                            <FontAwesomeIcon icon={faFileLines} className="csv-file-icon" />
                                            <div className="csv-file-details">
                                                <span className="csv-file-name">{file.name}</span>
                                                <span className="csv-file-size">{(file.size / 1024).toFixed(1)} KB</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="csv-dropzone-content">
                                            <FontAwesomeIcon icon={faCloudArrowUp} className="csv-dropzone-icon" />
                                            <span className="csv-dropzone-text">Drag &amp; drop your file here</span>
                                            <span className="csv-dropzone-hint">or click to browse — CSV only</span>
                                        </div>
                                    )}
                                </FileDropZone>
                            </div>
                        </>
                    )}

                    {error && (
                        <div className="csv-result-banner csv-result-error">
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                            <span>{error}</span>
                        </div>
                    )}

                    {rowErrors.length > 0 && (
                        <div className="csv-result-errors">
                            <div className="csv-result-errors-header">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                <span>{rowErrors.length} row(s) could not be read — nothing was changed:</span>
                            </div>
                            <div className="csv-result-errors-list">
                                {rowErrors.map((err, i) => (
                                    <div key={i} className="csv-error-row">
                                        <span className="csv-error-row-num">Row {err.row}</span>
                                        <span className="csv-error-field">{err.field}</span>
                                        <span className="csv-error-msg">{err.message}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {result && (() => {
                        // A file that only queues increases writes no profile,
                        // but something DID happen -- don't report it as a no-op.
                        const didSomething = result.updated > 0
                            || (result.summary?.increases_queued || 0) > 0;
                        return (
                        <div className={`csv-result-banner ${didSomething ? 'csv-result-success' : 'ccb-banner-neutral'}`}>
                            <FontAwesomeIcon icon={didSomething ? faCheckCircle : faInfoCircle} />
                            <span>{result.message}</span>
                        </div>
                        );
                    })()}

                    {summary && (
                        <div className="ccb-summary" data-testid="ccb-summary">
                            <div className="ccb-stat">
                                <span className="ccb-stat-value">{summary.matched}</span>
                                <span className="ccb-stat-label">partners matched</span>
                            </div>
                            <div className="ccb-stat">
                                <span className="ccb-stat-value">{summary.limits_lowered}</span>
                                <span className="ccb-stat-label">limits reduced</span>
                            </div>
                            <div className="ccb-stat ccb-stat-approval">
                                <span className="ccb-stat-value">{summary.increases_queued}</span>
                                <span className="ccb-stat-label">
                                    increases {result ? 'sent for approval' : 'need approval'}
                                </span>
                            </div>
                            <div className="ccb-stat ccb-stat-muted">
                                <span className="ccb-stat-value">{summary.unmatched}</span>
                                <span className="ccb-stat-label">not found</span>
                            </div>
                        </div>
                    )}

                    {plan.length > 0 && (
                        <div className="ccb-plan">
                            <div className="ccb-plan-header">
                                {preview ? 'Review before applying' : 'What was applied'}
                            </div>
                            <div className="ccb-plan-list">
                                {plan.map((entry) => {
                                    const meta = OUTCOME_META[entry.outcome] || OUTCOME_META.no_values;
                                    return (
                                        <div key={entry.row} className="ccb-plan-row">
                                            <div className="ccb-plan-row-head">
                                                <span className="ccb-plan-rownum">Row {entry.row}</span>
                                                <span className="ccb-plan-name">
                                                    {entry.matched_name || entry.name}
                                                </span>
                                                <span className={`ccb-badge ${meta.className}`}>
                                                    <FontAwesomeIcon icon={meta.icon} /> {meta.label}
                                                </span>
                                            </div>
                                            {entry.changes.length > 0 ? (
                                                <div className="ccb-plan-changes">
                                                    {entry.changes.map((change, i) => (
                                                        <div key={i} className="ccb-change">
                                                            <span className="ccb-change-field">{change.label}</span>
                                                            <span className="ccb-change-old">
                                                                {change.old === null ? '—' : change.old}
                                                            </span>
                                                            <FontAwesomeIcon icon={faArrowRight} className="ccb-change-arrow" />
                                                            <span className={`ccb-change-new ${change.action === 'approval' ? 'ccb-change-pending' : ''}`}>
                                                                {change.new}
                                                            </span>
                                                            {change.action === 'approval' && (
                                                                <span className="ccb-change-note">on approval</span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="ccb-plan-note">{entry.message}</div>
                                            )}
                                            {(entry.notes || []).map((note, i) => (
                                                <div key={`note-${i}`} className="ccb-plan-note">{note}</div>
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                <div className="csv-modal-footer">
                    {result ? (
                        <button type="button" className="csv-btn-upload" onClick={handleClose}>Done</button>
                    ) : preview ? (
                        <>
                            <button type="button" className="csv-btn-cancel" onClick={reset} disabled={busy}>
                                Choose another file
                            </button>
                            <button
                                type="button"
                                className="csv-btn-upload"
                                onClick={handleApply}
                                disabled={busy || summary?.updated === 0}
                            >
                                {busy
                                    ? <><FontAwesomeIcon icon={faSpinner} spin /> Applying...</>
                                    : <><FontAwesomeIcon icon={faCheckCircle} /> Apply to {summary?.updated || 0} partner(s)</>}
                            </button>
                        </>
                    ) : (
                        <>
                            <button type="button" className="csv-btn-cancel" onClick={handleClose}>Cancel</button>
                            <button
                                type="button"
                                className="csv-btn-upload"
                                onClick={handlePreview}
                                disabled={!file || busy}
                            >
                                {busy
                                    ? <><FontAwesomeIcon icon={faSpinner} spin /> Checking...</>
                                    : <><FontAwesomeIcon icon={faUpload} /> Preview changes</>}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CRMCreditBulkUploadModal;
